import { useState, useEffect } from "react";
import axiosInstance from "@/auth/axiosWithInterceptor";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type TimeSlot = {
  open: string;   // "HH:MM"
  close: string;  // "HH:MM"
};

export type DaySchedule = {
  day: string;    // "monday" … "sunday"
  closed: boolean;
  slots: TimeSlot[];
};

export type StoreSettings = {
  id: number;
  currency: string;
  tax_rate: number;
  allow_pickup: boolean;
  pickup_location: string | null;
  bank_transfer_entity: string | null;
  bank_transfer_bank: string | null;
  bank_transfer_iban: string | null;
  bank_transfer_bic_swift: string | null;
  working_hours: DaySchedule[] | null;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------------
// Module-level cache — one network request per page load regardless of how
// many components consume this hook simultaneously.
// ---------------------------------------------------------------------------

let _cache: StoreSettings | null = null;
let _inflight: Promise<StoreSettings> | null = null;

// All currently mounted hook instances register here so that when the admin
// saves new settings, every subscriber (footer, contact page, etc.) re-fetches
// immediately without a full page reload.
const _listeners = new Set<() => void>();

async function fetchStoreSettings(): Promise<StoreSettings> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;

  _inflight = axiosInstance
    .get<StoreSettings>("/store/settings")
    .then((res) => {
      _cache = res.data;
      _inflight = null;
      return _cache!;
    })
    .catch((err) => {
      _inflight = null;
      throw err;
    });

  return _inflight;
}

/**
 * Call after a successful admin save so every mounted hook instance
 * immediately re-fetches fresh data without a page reload.
 */
export function invalidateStoreSettingsCache(): void {
  _cache = null;
  _inflight = null;
  _listeners.forEach((notify) => notify());
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type UseStoreSettingsResult = {
  settings: StoreSettings | null;
  loading: boolean;
  error: boolean;
};

export function useStoreSettings(): UseStoreSettingsResult {
  const [settings, setSettings] = useState<StoreSettings | null>(_cache);
  const [loading, setLoading] = useState<boolean>(_cache === null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const doFetch = () => {
      if (cancelled) return;
      setLoading(true);
      setError(false);
      fetchStoreSettings()
        .then((data) => {
          if (!cancelled) {
            setSettings(data);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setError(true);
            setLoading(false);
          }
        });
    };

    // Initial load — skip network call if the cache is already warm.
    if (!_cache) {
      doFetch();
    }

    // Subscribe so that invalidateStoreSettingsCache() triggers a re-fetch in
    // every mounted consumer (footer, contact page, etc.) without a reload.
    _listeners.add(doFetch);

    return () => {
      cancelled = true;
      _listeners.delete(doFetch);
    };
  }, []);

  return { settings, loading, error };
}
